import React from 'react';
import { Modal, Tag, Typography, Space, Divider, Descriptions, Row, Col } from 'antd';
import { Lead } from '../services/api';
import { MailOutlined, PhoneOutlined, UserOutlined, CalendarOutlined, EnvironmentOutlined, BankOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';

const { Text, Title } = Typography;

// Utility function to get business area as array
function getBusinessAreaArray(business_area: any): string[] {
  if (!business_area) return [];
  if (Array.isArray(business_area)) return business_area;
  if (typeof business_area === 'string') {
    try {
      const parsed = JSON.parse(business_area);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return [business_area];
    }
  }
  return [String(business_area)];
}

interface LeadDetailsModalProps {
  open: boolean;
  lead: Lead | null;
  onClose: () => void;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ open, lead, onClose }) => {
  if (!lead) return null;
  const ba = getBusinessAreaArray(lead.business_area);
  
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };
  
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={900}
      zIndex={999999}
      maskClosable={false}
      destroyOnClose={true}
      title={
        <Space>
          <span style={{ color: '#1890ff', fontWeight: 600, ...getLeadCategoryStyle(lead.lead_category) }}>
            Detalhes do Lead: {lead.name}
          </span>
          <Tag color={ba.includes('energy_solutions') ? 'green' : 'blue'}>
            {ba.includes('energy_solutions') ? 'Energia' : 'Telecom'}
          </Tag>
        </Space>
      }
    >
      <Descriptions column={2} bordered size="small">
        {/* Basic Information */}
        <Descriptions.Item label="Nome" span={2}>
          <Text strong style={getLeadCategoryStyle(lead.lead_category)}>{lead.name}</Text>
        </Descriptions.Item>
        
        <Descriptions.Item label="Email">
          <Space>
            <MailOutlined />
            {lead.email || '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Telefone">
          <Space>
            <PhoneOutlined />
            {lead.phone || '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="NIF">
          <Space>
            <UserOutlined />
            {lead.dif || '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Empresa">
          {lead.company || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Área de Negócio" span={2}>
          {ba.includes('energy_solutions') ? 'Energia' : 'Telecomunicações'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Tipo de Cliente">
          {lead.customer_type === 'residential' ? 'Residencial' : 'Empresarial'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Status">
          <Tag color={lead.lead_status === 'Entrada' ? 'blue' : 
                      lead.lead_status === 'Feedback' ? 'orange' : 
                      lead.lead_status === 'Registro' ? 'green' : 
                      lead.lead_status === 'Instalados' ? 'purple' : 'default'}>
            {lead.lead_status || '-'}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Secção Atual" span={2}>
          <Tag color={lead.section === 'entrada' ? 'blue' : 
                      lead.section === 'feedback' ? 'orange' : 
                      lead.section === 'registro' ? 'green' : 
                      lead.section === 'instalados' ? 'purple' : 'default'}>
            {lead.section === 'entrada' ? 'Entrada' :
             lead.section === 'feedback' ? 'Feedback' :
             lead.section === 'registro' ? 'Registo e Documentos' :
             lead.section === 'instalados' ? 'Instalados' : lead.section || '-'}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Atribuído a">
          {lead.assigned_user_name || 'Não atribuído'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Criado por">
          {lead.created_user_name || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Data de Criação">
          <Space>
            <CalendarOutlined />
            {lead.created_at ? new Date(lead.created_at).toLocaleDateString('pt-PT') : '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Última Atualização">
          <Space>
            <CalendarOutlined />
            {lead.updated_at ? new Date(lead.updated_at).toLocaleDateString('pt-PT') : '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Data da Instalação" span={2}>
          <Space>
            <CalendarOutlined />
            {lead.installation_date ? new Date(lead.installation_date).toLocaleDateString('pt-PT') : '-'}
          </Space>
        </Descriptions.Item>
        
        {/* Address Information */}
        <Descriptions.Item label="Endereço" span={2}>
          <Space>
            <EnvironmentOutlined />
            {lead.address || '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Cidade">
          {lead.city || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Código Postal">
          {lead.postal_code || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="País">
          {lead.country || 'Portugal'}
        </Descriptions.Item>
        
        {/* Business Information */}
        <Descriptions.Item label="Fornecedor Atual">
          {lead.current_provider || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Data de Fim de Contrato">
          {lead.contract_end_date || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Próximo Follow-up">
          <Space>
            <ClockCircleOutlined />
            {lead.next_follow_up || '-'}
          </Space>
        </Descriptions.Item>
        
        <Descriptions.Item label="Última Tentativa de Contacto">
          <Space>
            <ClockCircleOutlined />
            {lead.last_contact_attempt || '-'}
          </Space>
        </Descriptions.Item>
        
        {/* Status-Specific Fields */}
        <Descriptions.Item label="Motivo Pendente" span={2}>
          {lead.pendente_reason || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Nota de Follow-up" span={2}>
          {lead.followup_note || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Nota Sem Interesse" span={2}>
          {lead.seminteresse_note || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Nota Informação Errada" span={2}>
          {lead.informacao_errada_note || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Sem Cobertura de" span={2}>
          {lead.sem_cobertura_de || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Fim da Fidelização" span={2}>
          {lead.end_of_contract || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

export default LeadDetailsModal; 